import { MouseEvent, useMemo, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { CircularProgress, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { CustomLayer, CustomLayerProps, Layer, ResponsiveLine } from "@nivo/line";
import ReportCard from "components/ReportCard";
import { buildOeeTrendPoints, IOeeTrendPoint, OeeMetricEnum } from "utilities/oeeSummary";
import { useStyles } from "./OeeTrendByDateRange.styles";
import { IOeeTrendByDateRangeProps } from "./OeeTrendByDateRangeInterfaces";

const TRANSLATION_PREFIX = "locations.oee_summary_dashboard.trend";
const TREND_METRICS: OeeMetricEnum[] = [
    OeeMetricEnum.OEE,
    OeeMetricEnum.AVAILABILITY,
    OeeMetricEnum.PERFORMANCE,
    OeeMetricEnum.QUALITY,
];
const PRODUCTION_DATE_FORMAT = "YYYY-MM-DD";
const DAY_LABEL_FORMAT: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
const MONTH_LABEL_FORMAT: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" };
/** The languages i18n.ts ships locales for, and the language it falls back to. */
const SUPPORTED_LANGUAGES = ["en", "es", "fr"];
const FALLBACK_LANGUAGE = "en";
const CHART_FONT_FAMILY = "Source Sans 3";
const CHART_MARGIN = { bottom: 40, left: 50, right: 56, top: 20 };
const Y_TICK_STEP = 10;
const Y_MIN_MAX = 100;
/** Most bands the y axis is allowed between 0 and its top, however far above 100 % the metric runs. */
const MAX_Y_TICK_BANDS = 10;
/** A 1-2-2.5-5 progression of the base step, so every label stays a round percentage at any height. */
const Y_TICK_STEPS = [Y_TICK_STEP, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000];
const MAX_X_TICKS = 16;
const AXIS_TICK_FONT_SIZE = 10;
const POINT_SIZE = 6;
const END_MARKER_RADIUS = 5;
const END_MARKER_LABEL_OFFSET = 10;
const END_MARKER_FONT_SIZE = 14;

/**
 * The detected language is whatever the browser is set to, so it can be one the app ships no locale for.
 * Intl would still format in it and label the axis in a language the rest of the page does not speak, so
 * the same fallback i18n.ts applies to the translations is applied here.
 */
const toSupportedLanguage = (language: string | undefined): string => {
    const base = (language ?? "").slice(0, 2).toLowerCase();

    return SUPPORTED_LANGUAGES.includes(base) ? base : FALLBACK_LANGUAGE;
};

const formatBucketLabel = ({ bucketStart, grain }: IOeeTrendPoint, language: string): string =>
    new Intl.DateTimeFormat(language, grain === "month" ? MONTH_LABEL_FORMAT : DAY_LABEL_FORMAT).format(
        moment(bucketStart, PRODUCTION_DATE_FORMAT).toDate(),
    );

/**
 * Every bucket label when they fit; otherwise every n-th one. The last bucket is always labelled, and the
 * regular label right before it is dropped when it would sit closer than one step.
 */
const pickTickLabels = (labels: string[]): string[] => {
    const step = Math.max(1, Math.ceil(labels.length / MAX_X_TICKS));
    const lastIndex = labels.length - 1;

    return labels.filter((_, index) => index === lastIndex || (index % step === 0 && lastIndex - index >= step));
};

/**
 * The finest round step that keeps the axis within MAX_Y_TICK_BANDS. Performance and OEE are uncapped, and
 * at the base 10 % step a 340 % peak would draw 35 gridlines over a card only a few hundred pixels tall.
 * A value past the last tabulated step falls back to an even split rounded up to the base step.
 */
const pickYTickStep = (axisMax: number): number =>
    Y_TICK_STEPS.find((step) => Math.ceil(axisMax / step) <= MAX_Y_TICK_BANDS) ??
    Math.ceil(axisMax / MAX_Y_TICK_BANDS / Y_TICK_STEP) * Y_TICK_STEP;

/**
 * Nivo 0.80 calls function layers as plain functions inside its own render
 * rather than mounting them as components, so the layer must stay hook-free and
 * receives its colors through this closure.
 */
const createEndMarkerLayer = (markerColor: string, labelColor: string): CustomLayer => {
    const EndMarkerLayer = ({ series }: CustomLayerProps) => {
        const lastMeasured = series
            .flatMap((serie) => serie.data)
            .filter(({ data, position }) => data.y !== null && data.y !== undefined && position.y !== null)
            .pop();

        if (!lastMeasured) {
            return null;
        }

        const { x, y } = lastMeasured.position;

        return (
            <g>
                <circle cx={x} cy={y} data-testid="oee-trend-end-marker" fill={markerColor} r={END_MARKER_RADIUS} />
                <text
                    data-testid="oee-trend-end-marker-label"
                    dominantBaseline="central"
                    fill={labelColor}
                    fontFamily={CHART_FONT_FAMILY}
                    fontSize={END_MARKER_FONT_SIZE}
                    fontWeight={700}
                    x={x + END_MARKER_LABEL_OFFSET}
                    y={y}
                >
                    {`${lastMeasured.data.y}%`}
                </text>
            </g>
        );
    };

    return EndMarkerLayer;
};

const OeeTrendByDateRange = ({ loading, productionLineName, range, rows }: IOeeTrendByDateRangeProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const { i18n, t } = useTranslation();
    const language = toSupportedLanguage(i18n.resolvedLanguage || i18n.language);
    const [metric, setMetric] = useState<OeeMetricEnum>(OeeMetricEnum.OEE);
    const { endDate, startDate } = range;

    const points = useMemo(
        () => buildOeeTrendPoints(rows, metric, { endDate, startDate }),
        [endDate, metric, rows, startDate],
    );

    const chart = useMemo(() => {
        const labels = points.map((point) => formatBucketLabel(point, language));
        const maxValue = Math.max(0, ...points.map(({ value }) => value ?? 0));
        const tickStep = pickYTickStep(Math.max(Y_MIN_MAX, maxValue));
        const yMax = Math.max(Y_MIN_MAX, Math.ceil(maxValue / tickStep) * tickStep);

        return {
            data: [{ data: points.map(({ value }, index) => ({ x: labels[index], y: value })), id: metric }],
            xTickValues: pickTickLabels(labels),
            yMax,
            yTickValues: Array.from({ length: Math.round(yMax / tickStep) + 1 }, (_, index) => index * tickStep),
        };
    }, [language, metric, points]);

    /**
     * Points are drawn on top of the line: a measured bucket between two gaps is a zero-length subpath the
     * line layer cannot paint, and a mill that does not run at weekends produces one of those every week.
     */
    const layers = useMemo<Layer[]>(
        () => [
            "grid",
            "axes",
            "lines",
            "points",
            createEndMarkerLayer(theme.palette.success.main, theme.palette.text.primary),
        ],
        [theme],
    );

    const chartTheme = useMemo(
        () => ({
            axis: {
                ticks: {
                    text: {
                        fill: theme.palette.text.primary,
                        fontFamily: CHART_FONT_FAMILY,
                        fontSize: AXIS_TICK_FONT_SIZE,
                    },
                },
            },
            fontFamily: CHART_FONT_FAMILY,
            grid: { line: { stroke: theme.palette.border.main, strokeWidth: 1 } },
            textColor: theme.palette.text.primary,
        }),
        [theme],
    );

    const handleMetricChange = (_: MouseEvent<HTMLElement>, value: OeeMetricEnum | null) => {
        if (value) {
            setMetric(value);
        }
    };

    const title = productionLineName
        ? t(`${TRANSLATION_PREFIX}.titles_with_production_line.${metric}`, { productionLine: productionLineName })
        : t(`${TRANSLATION_PREFIX}.titles.${metric}`);

    const metricToggle = (
        <ToggleButtonGroup
            className={classes.metricToggle}
            color="info"
            exclusive
            onChange={handleMetricChange}
            size="small"
            value={metric}
        >
            {TREND_METRICS.map((trendMetric) => (
                <ToggleButton key={trendMetric} value={trendMetric}>
                    {t(`${TRANSLATION_PREFIX}.metrics.${trendMetric}`)}
                </ToggleButton>
            ))}
        </ToggleButtonGroup>
    );

    return (
        <ReportCard
            headerActions={metricToggle}
            showNoData={!loading && points.every(({ value }) => value === null)}
            title={title}
        >
            {loading ? (
                <div className={classes.loadingWrapper}>
                    <CircularProgress color="info" size={24} />
                </div>
            ) : (
                <div className={classes.chartContainer}>
                    <ResponsiveLine
                        axisBottom={{ tickPadding: 5, tickSize: 5, tickValues: chart.xTickValues }}
                        axisLeft={{
                            format: (value) => `${value}%`,
                            tickPadding: 5,
                            tickSize: 5,
                            tickValues: chart.yTickValues,
                        }}
                        colors={[theme.palette.success.main]}
                        curve="linear"
                        data={chart.data}
                        enableArea={false}
                        enableGridX={false}
                        enableGridY={true}
                        enablePoints={true}
                        gridYValues={chart.yTickValues}
                        isInteractive={false}
                        layers={layers}
                        legends={[]}
                        lineWidth={3}
                        margin={CHART_MARGIN}
                        pointSize={POINT_SIZE}
                        theme={chartTheme}
                        xScale={{ type: "point" }}
                        yScale={{ max: chart.yMax, min: 0, stacked: false, type: "linear" }}
                    />
                </div>
            )}
        </ReportCard>
    );
};

export default OeeTrendByDateRange;
